import { PropsWithChildren, useMemo } from 'react'
import FormLayout from '../layouts/FormLayout'
import AssetDeviceTable from './device_table'
import Input from 'components/form/input'
import Button from 'components/button'
import ListBox from 'components/form/listbox'
import { useAdminGetAssetFilesQuery, useAdminListThemesQuery } from 'api'
import AssetFileTable from './file_table'
import FormError from 'components/error'
import ComboBox from 'components/form/combobox'
import { useGetClientsQuery } from 'api/endpoints/admin/client'
import { Client } from 'api/types/models/client'
import { RadioCardGroup } from 'components/form/radio_card_group'
import { RadioCardOption } from 'components/form/radio_card'
import ReactQuill from 'react-quill'
import { z } from 'zod'
import { FormBaseProps } from 'types/form'
import { UseFormReturn } from 'react-hook-form'
import Asset from 'api/types/models/asset'
import { Form } from 'components/form/form'
import { TimedMessage } from 'feature/message/components/items/timed-message'
import PostCodeLookup from 'components/form/postcode_lookup'
import { AssetType } from 'enums/AssetType'
import AssetInfoCard from './info_card'

const adminAssetSchema = z.object({
  client_id: z.coerce.number().nullable(),
  theme_id: z.coerce.number().nullable(),
  default_message_name: z.string().optional(),
  default_message_content: z.string().optional(),
  is_private: z.boolean(),
  is_activated: z.boolean(),
  description: z.string().optional(),
  line_1: z.string().nullable(),
  line_2: z.string().nullable(),
  town_or_city: z.string().nullable(),
  county: z.string().nullable(),
  country: z.string().nullable(),
  postcode: z.string().nullable(),
  note: z.string().nullable(),
  redirect_url: z.string().nullable(),
})

export type AdminAssetFormInputs = z.infer<typeof adminAssetSchema>

type AdminAssetFormProps = FormBaseProps<AdminAssetFormInputs> & {
  asset?: Asset | null
  isLoading: boolean
  btnTxt: string
}

function FormWrapper({
  asset,
  watch,
  control,
  trigger,
  register,
  getValues,
  setValue,
  formState: { errors, defaultValues },
  children,
}: PropsWithChildren<UseFormReturn<AdminAssetFormInputs>> & { asset?: Asset | null | undefined }) {
  const { data: clients, isLoading: isClientsLoading } = useGetClientsQuery({})
  const { data: themes } = useAdminListThemesQuery({ client_id: watch('client_id')! }, {
    skip: !watch('client_id'),
  })
  const { data: files } = useAdminGetAssetFilesQuery(asset?.id?.toString(), {
    skip: !asset?.id,
  })

  const themeOptions = useMemo(() => {
    return [
      { value: 0, alt: 'No Theme' },
      ...themes?.map((theme) => ({ value: theme.id, alt: theme.title })) ?? [],
    ]
  }, [themes])

  const clientOptions = useMemo(() => {
    const unpaginatedClients = clients as Client[]
    return unpaginatedClients?.map((client) => ({ value: client.id, text: client.display_name }))
  }, [clients])

  const toolbarOptions = [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'link'],
    [{ align: '' }, { align: 'center' }, { align: 'right' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }, { 'list': 'ordered' }, { 'list': 'bullet' }],
    ['clean'],
  ]

  const isKiCallAsset = asset?.product?.name.toLowerCase() === AssetType.KiCall.toLowerCase()

  return (
    <>
      <FormLayout>
        <FormLayout.Block>
          <FormLayout.Title
            title="Asset details"
            subtitle="Status, title and assignment for this asset"
          />

          <FormLayout.Group
            label="KiCall Activation Status"
            description={
              isKiCallAsset
                ? 'KiCall devices cannot be deactivated'
                : 'Specify if this asset is activated to be managed as a KiCall'
              }
            htmlFor="is_activated"
            intent="danger"
          >
            <RadioCardGroup
              name="is_activated"
              control={control}
              disabled={isKiCallAsset}
            >
              <RadioCardOption value={true} first>Activated</RadioCardOption>
              <RadioCardOption value={false} last>Deactivated</RadioCardOption>
            </RadioCardGroup>
            <FormError text={errors.is_activated?.message} />
          </FormLayout.Group>

          <FormLayout.Group
            label="Title"
            htmlFor="description"
            description="Set the title for this asset"
          >
            <Input {...register('description')} error={errors.description?.message} />
          </FormLayout.Group>

          <FormLayout.Group
            label="Inactive Redirect URL"
            htmlFor="redirect_url"
            description="When this asset is deactivated, scanners are sent here instead of the default destination. Leave blank to use the default. Has no effect while the asset is activated."
          >
            <div className="flex gap-2">
              <Input
                {...register('redirect_url')}
                error={errors.redirect_url?.message}
                placeholder="https://ark.life/resources"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() => setValue('redirect_url', '')}
              >
                Clear
              </Button>
            </div>
          </FormLayout.Group>

          <FormLayout.Group
            label="Client"
            htmlFor="client"
            description="Pre-filter themes and users by client"
          >
            <ComboBox
              isLoading={isClientsLoading}
              onChange={(option) => {
                if (option) {
                  setValue('client_id', option.value as number)
                  setValue('theme_id', 0) // No Theme Option
                }
              }}
              options={clientOptions}
              defaultValue={watch('client_id') as number}
              placeholder="Search for a client"
            />
            {
              errors.client_id?.message &&
              <FormError text={errors?.client_id?.message} className="mt-1 mb-2 !text-left" />
            }
          </FormLayout.Group>
          <FormLayout.Group
            label="Theme"
            htmlFor="theme"
            description="Set the theme assigned to this asset"
          >
            <ListBox
              key={watch('client_id')}
              defaultValue={asset?.theme?.id ?? undefined}
              {...register('theme_id')}
              options={themeOptions}
            />
          </FormLayout.Group>
        </FormLayout.Block>

        <FormLayout.Accordion
          title="Activated asset settings"
          subtitle="Messages, visibility and location — relevant once the asset is activated as a KiCall"
          defaultOpen={!!asset?.is_activated}
        >
          <FormLayout.Group
            label="Visibility"
            description="Specify who can view this asset"
            htmlFor="is_private"
          >
            <RadioCardGroup
              name="is_private"
              control={control}
            >
              <RadioCardOption value={false} first>Everyone</RadioCardOption>
              <RadioCardOption value={true} last>Certain People</RadioCardOption>
            </RadioCardGroup>
          </FormLayout.Group>

          <FormLayout.Group
            label="Default Message Name"
            htmlFor="default_message_name"
          >
            <Input {...register('default_message_name')} error={errors.default_message_name?.message} />
          </FormLayout.Group>

          <FormLayout.Group
            label="Default Message"
            htmlFor="default_message_content"
            description="Customize the default message"
          >
            <ReactQuill
              theme="snow"
              value={getValues('default_message_content')}
              placeholder="Write a message and use the toolbar above to style it"
              modules={{ toolbar: toolbarOptions }}
              onChange={(html) => {
                trigger('default_message_content')
                setValue('default_message_content', html, {
                  shouldDirty: getValues('default_message_content') !== defaultValues?.default_message_content,
                })
              }}
            />
            {
              errors?.default_message_content?.message &&
              <FormError text={errors.default_message_content.message} />
            }
          </FormLayout.Group>

          {
            asset &&
            <FormLayout.Group
              label="Timed Messages"
              description="Customise up to 3 timed messages for the asset"
            >
              {
                asset?.messages && asset?.messages.length > 1 &&
                <div className="flex flex-col gap-4">
                  {
                    asset?.messages?.filter((message) => !message.is_default).map((message) => {
                      return (
                        <TimedMessage
                          message={message}
                          key={`timed-message-${message.id}`}
                          to={`/admin/dashboard/assets/manage/${asset.id}/messages/${message.id}`}
                        />
                      )
                    })
                  }
                </div>
              }

              <div className="mt-4 w-full">
                <Button
                  block
                  type="link"
                  href={`/admin/dashboard/assets/manage/${asset.id}/messages/create`}
                  disabled={asset?.messages?.length === 4}
                >
                  Add new message
                </Button>
              </div>
            </FormLayout.Group>
          }

          <div className="pt-5 border-t border-gray-200">
            <FormLayout.Title
              title="Asset Location"
              subtitle="Assets location specified by address"
            />
          </div>

          <FormLayout.Group
            label="Address Lookup"
            description="Find and retrieve the assets's address"
          >
            <PostCodeLookup
              onSelect={(object) => {
                setValue('line_1', object.Line1)
                setValue('line_2', object.Line2)
                setValue('town_or_city', object.City)
                setValue('county', object.Province)
                setValue('postcode', object.PostalCode)
              }}
            />
          </FormLayout.Group>

          <FormLayout.Group
            label="Line 1"
            htmlFor="line_1"
          >
            <Input {...register('line_1')} error={errors.line_1?.message} />
          </FormLayout.Group>

          <FormLayout.Group
            label="Line 2"
            htmlFor="line_2"
          >
            <Input {...register('line_2')} error={errors.line_2?.message} />
          </FormLayout.Group>

          <FormLayout.Group
            label="City"
            htmlFor="town_or_city"
          >
            <Input {...register('town_or_city')} error={errors.town_or_city?.message} />
          </FormLayout.Group>

          <FormLayout.Group
            label="County"
            htmlFor="county"
          >
            <Input {...register('county')} error={errors.county?.message} />
          </FormLayout.Group>

          <FormLayout.Group
            label="Country"
            htmlFor="country"
          >
            <Input {...register('country')} error={errors.country?.message} />
          </FormLayout.Group>

          <FormLayout.Group
            label="Postcode"
            htmlFor="postcode"
          >
            <Input {...register('postcode')} error={errors.postcode?.message} />
          </FormLayout.Group>

          <FormLayout.Group
            label="Note"
            htmlFor="note"
            description="Define further locational details"
          >
            <Input {...register('note')} error={errors.note?.message} />
          </FormLayout.Group>

          {
            asset && asset?.user_id &&
            <>
              {
                files &&
                <div className="pt-5 border-t border-gray-200">
                  <FormLayout.Title
                    title="Available Files"
                    subtitle="Files available for the user which can be linked with their asset"
                  />
                  <div className="mt-4">
                    <AssetFileTable
                      userId={asset.user.id}
                      assetId={asset.id}
                      files={files.available_files}
                    />
                  </div>
                </div>
              }

              <div className="pt-5 border-t border-gray-200">
                <FormLayout.Title
                  title="Third Party Management"
                  subtitle="Third parties which are permitted to view this asset"
                />
                <div className="mt-4">
                  <AssetDeviceTable asset={asset} />
                </div>
              </div>
            </>
          }
        </FormLayout.Accordion>

        {children}
      </FormLayout>
    </>
  )
}

const AdminAssetForm = ({
  asset,
  onSubmit,
  isLoading,
  errors,
  defaultValues,
  btnTxt,
}: AdminAssetFormProps) => {
  return (
    <Form<AdminAssetFormInputs, typeof adminAssetSchema>
      error={errors}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      validationSchema={adminAssetSchema}
    >
      {(methods) => (
        <div className="flex flex-col-reverse lg:flex-row lg:items-start">
          <div className="flex-1 min-w-0">
            <FormWrapper
              {...methods}
              asset={asset}
            >
              {/* Mobile submit; on desktop the sticky buttons in the aside are used instead */}
              <FormLayout.Footer>
                <Button
                  type="submit"
                  isLoading={isLoading}
                  className="lg:hidden"
                >
                  {btnTxt}
                </Button>
              </FormLayout.Footer>
            </FormWrapper>
          </div>

          <aside className="px-4 pt-6 pb-6 lg:w-72 lg:flex-shrink-0 lg:px-0 lg:pr-10 lg:pt-10 lg:pb-0">
            <div className="lg:sticky lg:top-6">
              <AssetInfoCard asset={asset} />
              {/* Always-visible actions on desktop */}
              <Button
                type="submit"
                isLoading={isLoading}
                block
                className="hidden mt-3 lg:flex"
              >
                {btnTxt}
              </Button>
              <Button
                variant="secondary"
                href="/admin/dashboard/assets/browse"
                block
                className="hidden mt-2 lg:flex"
              >
                Cancel
              </Button>
            </div>
          </aside>
        </div>
      )}
    </Form>
  )
}

export default AdminAssetForm
